import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'framer-motion'
import { X, Phone, MapPin } from 'lucide-react'
import { salons } from '../../config/salons'
import { useCallStore } from '../../store/callStore'
import { track } from '../../lib/analytics/track'

export default function CallSalonModal() {
  const { t } = useTranslation()
  const isOpen = useCallStore((s) => s.isOpen)
  const close = useCallStore((s) => s.close)
  const setPreferred = useCallStore((s) => s.setPreferred)
  // Unchecked by default: remembering the salon is opt-in, so the user makes a
  // deliberate choice rather than having a preference persisted silently.
  const [remember, setRemember] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    track('call_salon_open')
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [isOpen, close])

  const handleSelect = (salonId: string, phone: string) => {
    track('call_salon_click', { salon_id: salonId })
    setPreferred(salonId, remember)
    close()
    window.location.assign(`tel:${phone.replace(/\s/g, '')}`)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          aria-modal="true"
          role="dialog"
          aria-labelledby="call-modal-title"
        >
          {/* Backdrop */}
          <button
            type="button"
            aria-label={t('callModal.close')}
            onClick={close}
            className="absolute inset-0 bg-mocha/80 backdrop-blur-sm"
          />

          {/* Panel */}
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="relative w-full max-w-md bg-ivory border border-gold/30 shadow-[0_20px_60px_rgba(45,28,18,0.35)]"
          >
            {/* Top gold rule */}
            <div className="h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />

            <div className="px-6 pt-7 pb-6 md:px-8 md:pt-8 md:pb-7">
              <div className="flex items-start justify-between gap-4 mb-6">
                <div>
                  <p className="font-body text-[11px] tracking-[0.2em] uppercase text-gold/80 mb-2">
                    {t('callModal.eyebrow')}
                  </p>
                  <h2
                    id="call-modal-title"
                    className="font-display text-2xl md:text-3xl text-mocha leading-tight"
                  >
                    {t('callModal.title')}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={close}
                  aria-label={t('callModal.close')}
                  className="p-1 text-mocha/50 hover:text-gold transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <ul className="space-y-2.5">
                {salons.map((salon) => (
                  <li key={salon.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(salon.id, salon.phone)}
                      className="group relative w-full text-left overflow-hidden border border-mocha/15 hover:border-gold bg-ivory hover:bg-gold/5 px-4 py-3.5 transition-all duration-300"
                    >
                      {/* Diagonal sheen on hover */}
                      <span
                        className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 ease-out pointer-events-none"
                        style={{
                          background:
                            'linear-gradient(115deg, transparent 35%, rgba(198,168,107,0.25) 50%, transparent 65%)',
                        }}
                        aria-hidden="true"
                      />
                      <div className="relative flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-display text-base text-mocha">
                            {t('callModal.salonOption', { name: salon.name })}
                          </p>
                          <p className="font-body text-xs text-mocha/60 mt-0.5 flex items-center gap-1.5">
                            <MapPin size={11} className="text-gold/70 shrink-0" />
                            <span className="truncate">
                              {salon.address}, {salon.postalCode} {salon.city}
                            </span>
                          </p>
                        </div>
                        <span className="font-body text-sm text-gold inline-flex items-center gap-1.5 shrink-0">
                          <Phone size={13} aria-hidden="true" />
                          {salon.phone}
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>

              {/* Remember */}
              <div className="mt-6 pt-5 border-t border-mocha/10">
                <label className="flex items-center gap-2.5 cursor-pointer group select-none">
                  <span className="relative inline-flex items-center justify-center w-4 h-4 border border-mocha/40 group-hover:border-gold transition-colors">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="absolute inset-0 opacity-0 cursor-pointer"
                    />
                    {remember && (
                      <span className="block w-2 h-2 bg-gold" aria-hidden="true" />
                    )}
                  </span>
                  <span className="font-body text-xs tracking-wide text-mocha/70 group-hover:text-mocha transition-colors">
                    {t('callModal.rememberChoice')}
                  </span>
                </label>
              </div>
            </div>

            {/* Bottom gold rule */}
            <div className="h-px bg-gradient-to-r from-transparent via-gold/60 to-transparent" />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
